/**************************************************************************************************/
/** @import Node Packages                                                                        **/
/**************************************************************************************************/
import { defineComponent } from 'vue'

/**************************************************************************************************/
/** @import Components/Global                                                                    **/
/**************************************************************************************************/
import * as CompanyLogo from '@/Components/Global/CompanyLogo/template.vue'
import * as SubmitButton from '@/Components/Global/SubmitButton/template.vue'

/**************************************************************************************************/
/** @import Constants                                                                            **/
/**************************************************************************************************/
import { Documents } from '@/Constants/Documents'
import { VueRouter } from '@/Constants/VueRouter'
import { Vuex } from '@/Constants/Vuex'

/**************************************************************************************************/
/** @import Helpers/Utils                                                                        **/
/**************************************************************************************************/
import { StoreHelper } from '@/Helpers/Utils/StoreHelper'

/**************************************************************************************************/
/** @import Mixins                                                                               **/
/**************************************************************************************************/
import MixinComponent from '@/Mixins/MixinComponent'
import MixinResponsive from '@/Mixins/MixinResponsive'

/**************************************************************************************************/
/** @import Store/Global                                                                         **/
/**************************************************************************************************/
import Store from '@/Store/Global/Default'

/**************************************************************************************************/
/** @internal Const @name InitialsButton                                                         **/
/**************************************************************************************************/
const InitialsButton = defineComponent(
{
	/*************************************************/
	/** @Properties                                 **/
	/*************************************************/
	name: 'InitialsButton', mixins: [MixinComponent, MixinResponsive],

	components:
	{
		CompanyLogo: CompanyLogo.default,
		SubmitButton: SubmitButton.default
	},

	/*************************************************/
	/** @Data                                       **/
	/*************************************************/
	data()
	{
		return {
			initials: '??',
			popupMenu:
			{
				eventAttached: false,
				items: [] as Array<any>,
				show: false
			},
			states:
			{
				isFetching: false,
				options: ['Administración', 'Servicios', 'CheckLists'],
				wasAddedAtLeastOneSystem: false
			}
		}
	},

	/*************************************************/
	/** @Hooks                                      **/
	/*************************************************/
	mounted() : void
	{
		// Actualiza el valor de 'initials' si el usuario a iniciado sesión correctamente.
		const user = Store.getters.getStoredUser
		if (user)
		{
			const nChar = user?.name ? user.name.substring(0, 1).toUpperCase() : ''
			const aChar = user?.pLastName ? user.pLastName.substring(0, 1).toUpperCase() : ''
			this.initials = nChar + aChar
		}

		// Inicializar los Items del Menu.
		this.InitPopupMenuItems()
	},

	/*************************************************/
	/** @Methods                                    **/
	/*************************************************/
	methods:
	{
		async CallbackLogOut() : Promise<void>
		{
			Store.commit('destroyCredentials')
			this.OnDetachDocumentMouseClick()
			this.$router.push({ name: VueRouter.Views.Global.LOGIN.NAME, params: { successLogout: 'true' } })
		},

		HasPermissionToSystem(accessKeyName: string) : any
		{
			const userPermissions = Store.getters.getStoredUserPermissionsAsObject
			if (!userPermissions) return null

			const permission = userPermissions[accessKeyName]?.privileges.read
			if (permission) this.Set<InitialsButtonRef['states']>({ wasAddedAtLeastOneSystem: true })
			return permission
		},

		IncludeMainItems() : void
		{
			const { items } = this.popupMenu
			items.push({ type: 'link', icon: 'user', text: 'Mi Perfil', to: VueRouter.Views.Global.USER_PROFILE.NAME })
			items.push({ type: 'divider' })
		},

		IncludeSystemItems() : void
		{
			const { items } = this.popupMenu
			const user = Store.getters.getStoredUser

			if (user?._idRole === Documents.Roles.Kreis.CLIENT)
			{
				if (this.HasPermissionToSystem('ACCESS_MODULE_40')) items.push({
					type: 'link', icon: 'external-link-square-alt', text: 'Modulo CheckLists', to: VueRouter.Views.Modules.CheckList.MainView.NAME
				})
			}
			else
			{
				if (this.HasPermissionToSystem('ACCESS_MODULE_20')) items.push({
					type: 'link', icon: 'external-link-square-alt', text: 'Modulo Administración', to: VueRouter.Views.Modules.Administration.ExternalUsersView.NAME
				})
				if (this.HasPermissionToSystem('ACCESS_MODULE_30')) items.push({
					type: 'link', icon: 'external-link-square-alt', text: 'Modulo Servicios', to: VueRouter.Views.Modules.SERVICES_CPANEL.NAME
				})
				if (this.HasPermissionToSystem('ACCESS_MODULE_40')) items.push({
					type: 'link', icon: 'external-link-square-alt', text: 'Modulo CheckLists', to: VueRouter.Views.Modules.CheckList.MainView.NAME
				})
				if (this.HasPermissionToSystem('ACCESS_MODULE_41')) items.push({
					type: 'link', icon: 'external-link-square-alt', text: 'Modulo Setup CheckLists', to: VueRouter.Views.Modules.CheckList.SetupView.NAME
				})
			}
			if (this.states.wasAddedAtLeastOneSystem) items.push({ type: 'divider' })
		},

		InitPopupMenuItems() : void
		{
			// Asegurarse de vaciar el Array completo antes de empezar a agregar items.
			this.popupMenu.items = []

			// Nombre de Usuario.
			const { items } = this.popupMenu
			const user = Store.getters.getStoredUser
			const text = user ? `${ user?.name } ${ user?.pLastName }` : '??'
			items.push({ type: 'title', text })

			// Incluir el resto de elementos.
			this.IncludeMainItems()
			this.IncludeSystemItems()

			// El ultimo item debe ser para Cerrar Sesión.
			items.push({ type: 'link', icon: 'sign-out-alt', text: 'Cerrar Sesión', preventDefaults: true, callback: this.CallbackLogOut })
		},

		OnAttachDocumentMouseClick() : void
		{
			this.popupMenu.eventAttached = true
			document.addEventListener("click", this.OnDocumentMouseClick)
		},

		OnDetachDocumentMouseClick() : void
		{
			this.popupMenu.show = false
			this.popupMenu.eventAttached = false
			document.removeEventListener("click", this.OnDocumentMouseClick)
		},

		OnDocumentMouseClick({ target }: any) : void
		{
			if (target.parentElement === null) return
			if (target.parentElement.id === 'initials-button') return
			if (target.parentElement.id === 'popupMenu') return
			if (target instanceof HTMLSpanElement) return
			this.OnDetachDocumentMouseClick()
		},

		OnIBSendReport() : void
		{

		},

		OnPopupMenuItemLinkClick(item: { callback: Function, preventDefaults: boolean, to: string }) : void
		{
			if (item?.preventDefaults === true)
			{
				item.callback()
			}
			else
			{
				if (this.$route.name !== item.to)
				{
					this.OnDetachDocumentMouseClick()
					this.$router.replace({ name: item.to })
				}
			}
		},

		OnTogglePopupMenu() : void
		{
			this.popupMenu.show = !this.popupMenu.show
			if (this.popupMenu.show === true && this.popupMenu.eventAttached === false)
			{
				this.OnAttachDocumentMouseClick()
			}
			else
			{
				this.OnDetachDocumentMouseClick()
			}
		}
	},

	/*************************************************/
	/** @Watch                                      **/
	/*************************************************/
	watch:
	{
		...StoreHelper.watchStoreProperty(Vuex.Modules.Global.Names.Permissions, 'InitPopupMenuItems')
	}
})

/**************************************************************************************************/
/** @export Component @name InitialsButton                                                       **/
/**************************************************************************************************/
export default InitialsButton

/**************************************************************************************************/
/** @export Type @name InitialsButtonRef                                                         **/
/**************************************************************************************************/
export type InitialsButtonRef = InstanceType<typeof InitialsButton>